import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import { it as itLocale, enUS } from 'date-fns/locale'
import { CalendarIcon } from 'lucide-react'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Calendar } from '../ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { cn } from '../../lib/utils'
import { WorkoutResultDetailsEditor } from './WorkoutResultDetailsEditor'
import { buildDetailsFromTemplates, summarizeCompletion } from '../../utils/workout-result-details'
import type { ExerciseTemplate } from '../../types/exercise-template'
import type { ResultTemplateBlock, WorkoutResultPayload } from '../../types/workout'

const EFFORT_KEYS = [
  'trainingDiary.effort.veryEasy',
  'trainingDiary.effort.easy',
  'trainingDiary.effort.moderate',
  'trainingDiary.effort.hard',
  'trainingDiary.effort.veryHard',
]

const EFFORT_SELECTED_CLASSES = [
  'bg-yellow-400 border-yellow-400 text-yellow-950',
  'bg-blue-500 border-blue-500 text-white',
  'bg-green-500 border-green-500 text-white',
  'bg-orange-400 border-orange-400 text-white',
  'bg-red-500 border-red-500 text-white',
]

const COMPLETION_VALUES: Array<25 | 50 | 75 | 100> = [25, 50, 75, 100]

export interface WorkoutResultFormInitial {
  completed_on?: string | null
  effort?: number | null
  completion_percent?: 25 | 50 | 75 | 100 | null
  note?: string | null
  details?: ResultTemplateBlock[] | null
}

interface WorkoutResultFormProps {
  initial?: WorkoutResultFormInitial
  templates?: ExerciseTemplate[]
  onSave: (payload: WorkoutResultPayload) => Promise<void>
  onCancel?: () => void
}

function todayIso(): string {
  return format(new Date(), 'yyyy-MM-dd')
}

export function WorkoutResultForm({ initial, templates, onSave, onCancel }: WorkoutResultFormProps) {
  const { t, i18n } = useTranslation()
  const today = todayIso()
  const todayDate = new Date()
  todayDate.setHours(0, 0, 0, 0)

  const initialDetails = useMemo<ResultTemplateBlock[]>(() => {
    if (initial?.details && initial.details.length > 0) return initial.details
    if (templates && templates.length > 0) return buildDetailsFromTemplates(templates)
    return []
  }, [initial?.details, templates])

  const [completedOn, setCompletedOn] = useState<string>(initial?.completed_on ?? today)
  const [effort, setEffort] = useState<number>(initial?.effort ?? 3)
  const [completion, setCompletion] = useState<25 | 50 | 75 | 100>(initial?.completion_percent ?? 100)
  const [note, setNote] = useState(initial?.note ?? '')
  const [details, setDetails] = useState<ResultTemplateBlock[]>(initialDetails)
  const [saving, setSaving] = useState(false)
  const [calendarOpen, setCalendarOpen] = useState(false)

  const dateFnsLocale = i18n.language?.startsWith('it') ? itLocale : enUS
  const selectedDate = completedOn ? parseISO(completedOn) : undefined
  const detailedMode = details.length > 0
  const derivedCompletion = detailedMode ? summarizeCompletion(details) : completion

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const payload: WorkoutResultPayload = {
        completed_on: completedOn,
        effort,
        note: note.trim() || null,
      }
      if (detailedMode) {
        payload.details = details
      } else {
        payload.completion_percent = completion
      }
      await onSave(payload)
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 rounded-md border p-3 bg-card">
      <div className="flex flex-col gap-2">
        <Label htmlFor="result-date">{t('trainingDiary.workouts.result.dateLabel', 'Date')}</Label>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              id="result-date"
              type="button"
              variant="outline"
              className={cn(
                'justify-start text-left font-normal',
                !selectedDate && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 size-4" />
              {selectedDate
                ? format(selectedDate, 'PPP', { locale: dateFnsLocale })
                : t('common.pickDate', 'Pick a date')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => {
                if (d) setCompletedOn(format(d, 'yyyy-MM-dd'))
                setCalendarOpen(false)
              }}
              disabled={(d) => d > todayDate}
              defaultMonth={selectedDate}
              autoFocus
            />
          </PopoverContent>
        </Popover>
        <span className="text-xs text-muted-foreground">
          {t('trainingDiary.workouts.result.dateHint', 'Today or earlier')}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <Label>{t('trainingDiary.workouts.result.effortLabel', 'Effort')}</Label>
        <div className="flex gap-1">
          {EFFORT_KEYS.map((key, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setEffort(idx + 1)}
              className={cn(
                'flex-1 py-1.5 rounded-md border text-xs transition-colors flex flex-col items-center',
                effort === idx + 1
                  ? EFFORT_SELECTED_CLASSES[idx]
                  : 'bg-background text-foreground border-border hover:bg-accent'
              )}
            >
              <span className="font-medium">{idx + 1}</span>
              <span className="text-[10px] leading-tight">{t(key)}</span>
            </button>
          ))}
        </div>
      </div>

      {detailedMode ? (
        <>
          <WorkoutResultDetailsEditor details={details} onChange={setDetails} />
          <div className="text-xs text-muted-foreground">
            {t('trainingDiary.workouts.result.completionDerivedLabel', 'Completion (auto)')}: <span className="font-medium">{derivedCompletion}%</span>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-2">
          <Label>{t('trainingDiary.workouts.result.completionLabel', 'Completion')}</Label>
          <div className="grid grid-cols-4 gap-1.5">
            {COMPLETION_VALUES.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setCompletion(v)}
                className={cn(
                  'py-2 rounded-md border text-sm font-medium transition-colors',
                  completion === v
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-accent'
                )}
              >
                {v}%
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Label htmlFor="result-note">{t('trainingDiary.workouts.result.noteLabel', 'Note (optional)')}</Label>
        <Textarea
          id="result-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={2000}
          rows={3}
        />
      </div>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel', 'Cancel')}
          </Button>
        )}
        <Button type="submit" disabled={saving}>
          {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
        </Button>
      </div>
    </form>
  )
}
